import {
    DndContext,
    DragOverlay,
    type DragStartEvent,
    MouseSensor,
    PointerSensor,
    pointerWithin,
    useSensor,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import * as React from 'react';

import { PortalAbsolute } from '@gitbook/react-uikit';

import { DragCardPreview } from './DragCardPreview';
import type { DNDItem } from './types';

export function DndProvider(props: { children: React.ReactNode }) {
    const [active, setActive] = React.useState<DNDItem | null>(null);

    const pointerSensor = useSensor(PointerSensor, {
        activationConstraint: {
            distance: 10,
        },
    });

    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 10,
        },
    });

    const onDragStart = React.useCallback(({ active }: DragStartEvent) => {
        // This global style is used to apply `hideOnDrag`, see: DragAndDrop.module.css
        window.document.body.classList.add('dragging');
        setActive(active.data.current as DNDItem);
    }, []);

    const onDragEnd = React.useCallback(() => {
        window.document.body.classList.remove('dragging');
        // no logic here, as we use useDroppableWithCallback to localize drop actions
        setActive(null);
    }, []);

    return (
        <DndContext
            sensors={[pointerSensor, mouseSensor]}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragEnd}
            /**
             * The default collision algorithm will not work for some ToC lines where the
             * Draggable elements have bigger rects than the droppable areas.
             * Here we switch to using pointerWithin, which is not affected by draggable
             * and droppable not fully intersecting.
             *
             * See docs:
             * https://docs.dndkit.com/api-documentation/context-provider/collision-detection-algorithms#pointer-within
             */
            collisionDetection={pointerWithin}
        >
            {props.children}
            <PortalAbsolute>
                <DragOverlay
                    modifiers={[snapCenterToCursor]}
                    /**
                     * Drop animations cannot properly work for our current implementation.
                     *
                     * They require the DragOverlay, or items being dragged, actually moving
                     * to the new destination in the DOM without unmounting.
                     *
                     * See this discussion for more info:
                     * https://github.com/clauderic/dnd-kit/discussions/409
                     */
                    dropAnimation={null}
                >
                    {active ? <DragCardPreview item={active} /> : null}
                </DragOverlay>
            </PortalAbsolute>
        </DndContext>
    );
}
